import React, { useMemo } from 'react';
import { Link } from "react-router-dom";
import { selectAllOfficeEntities } from '@features/desks/store/advanceHotDeskingSetup.duck';
import { getRootOfficeById } from '@shared/utils/OfficeHelper';
import { isCompanyAdminUser, isOfficeAdminUser } from '@shared/utils/AccessControl';
import styled from "styled-components";
import Colours from '@shared/ui/atoms/Colours';
import Icon, { IconTypes } from '@shared/ui/atoms/icon/Icon';
import SidebarToggleButton from "./SidebarToggleButton";

import Calendar from '@shared/ui/molecules/calendar/Calendar';
import moment, { Moment } from "moment";
import { useDispatch, useSelector } from "react-redux";
import { selectCalendarDate, setCalendarDate } from '@shared/store/dashboard.duck';
import { selectActiveDay } from '@features/whereabouts/store/editMovements.duck';
import { selectCurrentUser } from '@shared/store/auth.duck';
import { selectCurrentUsersWhereabouts } from '@features/whereabouts/pages/team-movements/models/team-movements.duck';
import { BodySmall } from '@shared/ui/atoms/fonts/Body';
import { useTranslation } from "react-i18next";
import { isInTeams } from "@shared/utils/TeamsUtils";
import { ScopeActions } from "./ScopeActions";
import { tablet } from "@shared/ui/atoms/MediaQueries";
import { whereaboutsHeaderOffset, whereaboutsViewportHeight } from "./Structure";
import { PageView } from "./types";
import {
  selectGlobalHeaderCollapsed,
  selectPageView,
  selectSchengenCountsVisible,
  setPageView,
  toggleSchengenCounts
} from "@features/whereabouts/store/whereaboutsView.duck";
import { Switch } from '@shared/ui/atoms/Switch';
import { DialogIdentifiers, openDialog } from '@shared/store/dialog.duck';
import { OutlineButton } from '@shared/ui/atoms/buttons/OutlineButton';
import { MicrosoftCalendarSyncActiveIndicator } from '@shared/ui/organisms/MicrosoftCalendarSyncActiveIndicator';

interface Props {
  isOpen: boolean;
  onToggle: () => void;
  onWeekNotesClick?: () => void;
}

export default function WhereaboutsSidebar({ isOpen, onToggle, onWeekNotesClick }: Props) {
  const dispatch = useDispatch();
  const {t} = useTranslation();
  const calendarDate = useSelector(selectCalendarDate);
  const activeDate = useSelector(selectActiveDay);
  const currentUser = useSelector(selectCurrentUser);
  const pageView = useSelector(selectPageView);
  const schengenCountsVisible = useSelector(selectSchengenCountsVisible);
  const schengenTrackingEnabled = !!currentUser?.companyEntity?.schengenTracking;
  const approvalsEnabled = currentUser?.companyEntity.holidayApprovalEnabled;
  const currentUsersWhereabouts = useSelector(selectCurrentUsersWhereabouts);
  const approvalCount = currentUsersWhereabouts?.pendingApprovalCount ?? 0;
  const headerCollapsed = useSelector(selectGlobalHeaderCollapsed);
  const allOffices = useSelector(selectAllOfficeEntities);
  const canViewParking = useMemo(() => {
    if (!currentUser) return false;
    if (!isCompanyAdminUser(currentUser) && !isOfficeAdminUser(currentUser)) return false;
    const rootOffice = getRootOfficeById(allOffices, currentUser.defaultLocationId);
    return !!rootOffice?.hasCarParkPlan;
  }, [allOffices, currentUser]);

  const handleCalendarDateSelect = (date: Moment) => {
    dispatch(setCalendarDate(date));
  };

  return (
    <>
      <CollapsedRail $isOpen={isOpen} $isInTeams={isInTeams()} $headerCollapsed={headerCollapsed}>
        <SidebarToggleButton onClick={onToggle} isOpen={isOpen} />
      </CollapsedRail>
      <SidebarContainer $isOpen={isOpen} $isInTeams={isInTeams()} $headerCollapsed={headerCollapsed}>
        <SidebarToggleButton onClick={onToggle} isOpen={isOpen} />
        <SidebarContent>
          <Calendar initialDate={calendarDate || moment()}
                    activeDate={activeDate}
                    onDateSelect={handleCalendarDateSelect}
                    style={{marginBottom: 24}}
                    highlightToday={true} />

          <OutlineButton click={() => dispatch(openDialog(DialogIdentifiers.AddProjectOrEventDialog))}
                         text="whereabouts-v2.add-project-or-event"
                         fullWidth={true}
                         style={{marginBottom: 8}} />

          <ScopeActions/>

          <SegmentedToggle>
            <SegmentOption $active={pageView === PageView.Calendar} onClick={() => dispatch(setPageView(PageView.Calendar))}>
              {t('whereabouts-v2.calendar')}
            </SegmentOption>
            <SegmentOption $active={pageView === PageView.Grid} onClick={() => dispatch(setPageView(PageView.Grid))}>
              {t('whereabouts-v2.grid')}
            </SegmentOption>
          </SegmentedToggle>
          {schengenTrackingEnabled && (
            <SchengenCountsRow>
              <Switch value={schengenCountsVisible}
                      label={'whereabouts-v2.schengen-counts'}
                      weight={600}
                      onChange={() => dispatch(toggleSchengenCounts())} />
            </SchengenCountsRow>
          )}
          <NavLinks>
            {approvalsEnabled && <NavLink as={Link} to="/holidays/view-approvals">
              <IconWrapper>
                <img src="/assets/icons/font/Notification.svg" alt="" style={{width: '1.2em', height: '1.2em'}}/>
                {approvalCount > 0 && <ApprovalBadge>{approvalCount > 9 ? '9+' : approvalCount}</ApprovalBadge>}
              </IconWrapper>
              <NavLabel>{t('whereabouts-v2.approvals-required')}</NavLabel>
            </NavLink>}
            <NavLink as="button" type="button" onClick={onWeekNotesClick}>
              <Icon icon={IconTypes.Comments}/>
              <NavLabel>{t('week-notes.week-notes')}</NavLabel>
            </NavLink>
            <NavLink as={Link} to="/whereabouts/my-summary">
              <Icon icon={IconTypes.Reporting}/>
              <NavLabel>{t('whereabouts-v2.my-whereabouts-report')}</NavLabel>
            </NavLink>
            {canViewParking && <NavLink as={Link} to="/whereabouts/office-view">
              <img src="/assets/icons/car-outline.svg" alt="" style={{width: '1.2em', height: '1.2em'}}/>
              <NavLabel>{t('whereabouts-v2.view-parking')}</NavLabel>
            </NavLink>}
            <NavLink as={Link} to="/whereabouts/preferences">
              <Icon icon={IconTypes.Profile}/>
              <NavLabel>{t('whereabouts-v2.profile-and-favourites')}</NavLabel>
            </NavLink>
          </NavLinks>

          <MicrosoftCalendarSyncActiveIndicator style={{marginTop: 24}} />
        </SidebarContent>
      </SidebarContainer>
    </>
  );
}

const SidebarContent = styled.div`
  padding: 0px 16px 60px 16px;
  max-width: 300px;
  margin: 0 auto;
`;

const SchengenCountsRow = styled.div`
  margin-bottom: 16px;
`;

const SegmentedToggle = styled.div`
  display: flex;
  background: #EEEFF7;
  border-radius: 100px;
  padding: 4px;
  margin-top: 8px;
  margin-bottom: 16px;
  max-width: 240px;
`;

const SegmentOption = styled.button<{ $active: boolean }>`
  flex: 1;
  padding: 6px 16px;
  border-radius: 100px;
  border: ${props => props.$active ? `1px solid rgba(0, 0, 0, 0)` : 'none'};
  background: ${props => props.$active ? '#FFFFFF' : 'transparent'};
  color: ${props => props.$active ? Colours.blue : Colours.black};
  font-size: 14px;
  font-weight: ${props => props.$active ? 600 : 400};
  font-family: "proxima-nova", sans-serif;
  cursor: pointer;
  box-shadow: ${props => props.$active ? '0 4px 9px 0 #CACEED' : 'none'};
  transition: all 0.2s ease, border 0s ease;

  &:hover {
    color: ${Colours.blue};
    font-weight: 600;
  }
`;

const NavLabel = styled(BodySmall)`
  font-weight: 600;
`;

const IconWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const ApprovalBadge = styled.span`
  position: absolute;
  top: -8px;
  left: -8px;
  background: ${Colours.blue};
  color: #FFFFFF;
  font-family: proxima-nova;
  font-style: normal;
  font-size: 11px;
  line-height: 18px;
  font-weight: 700;
  min-width: 18px;
  height: 18px;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
`;

const NavLinks = styled.div`
  margin-top: 16px;
  display: flex;
  flex-direction: column;
`;

const NavLink = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 9px;
  cursor: pointer;
  border-radius: 6px;
  background: none;
  font: inherit;
  text-align: left;
  width: 100%;
  border: none;
  border-bottom: 1px solid ${Colours.iconGrey};
  text-decoration: none;
  .icon {
    font-size: 1.2em;
  }
  &:last-child {
    border-bottom: none;
  }
  &:hover {
    background: ${Colours.veryLightGrey};
  }
`;

const CollapsedRail = styled.div<{ $isOpen: boolean; $isInTeams: boolean; $headerCollapsed: boolean }>`
  position: fixed;
  right: 0;
  top: ${props => whereaboutsHeaderOffset(props.$isInTeams, props.$headerCollapsed)};
  width: 80px;
  height: ${props => whereaboutsViewportHeight(props.$isInTeams, props.$headerCollapsed)};
  background: #FFFFFF;
  border-left: 1px solid rgba(0, 0, 0, 0.05);
  display: none;
  flex-direction: column;
  align-items: center;
  padding-top: 16px;
  gap: 8px;
  z-index: 1099;
  opacity: ${props => props.$isOpen ? 0 : 1};
  pointer-events: ${props => props.$isOpen ? 'none' : 'auto'};
  transition: opacity 0.2s ease, top 0.2s ease, height 0.2s ease;
  @media (${tablet}) {
    display: flex;
  }
`;

const SidebarContainer = styled.div<{ $isOpen: boolean; $isInTeams: boolean; $headerCollapsed: boolean }>`
  right: 0;
  top: ${props => whereaboutsHeaderOffset(props.$isInTeams, props.$headerCollapsed)};
  width: ${props => props.$isOpen ? '360px' : '0'};
  height: ${props => whereaboutsViewportHeight(props.$isInTeams, props.$headerCollapsed)};
  background: #FFFFFF;
  box-shadow: -2px 0 8px rgba(46, 74, 148, 0.06);
  border-left: 1px solid rgba(0, 0, 0, 0.05);
  overflow-y: auto;
  z-index: 1100;
  transform: ${props => props.$isOpen ? 'translateX(0)' : 'translateX(100%)'};
  transition: transform 0.25s ease, top 0.2s ease, height 0.2s ease;
`;
